import { Link } from 'react-router-dom';
import { servicesMeta } from '../../../mocks/services';

const serviceImages: Record<string, string> = {
  installation: 'https://public.readdy.ai/ai/img_res/6e96a058e01a06227c7aa51f476069d0.jpg',
  refinishing: 'https://readdy.ai/api/search-image?query=hardwood%20floor%20refinishing%20sanding%20gleaming%20restored%20oak%20floors%20beautiful%20finish%20Maryland%20luxury%20home%20warm%20afternoon%20light%20polished%20result&width=600&height=400&seq=grid-ref-1&orientation=landscape',
  repair: 'https://readdy.ai/api/search-image?query=seamless%20hardwood%20floor%20repair%20matching%20grain%20color%20restored%20wood%20floor%20Maryland%20home%20natural%20light%20invisible%20repair%20professional%20result&width=600&height=400&seq=grid-rep-1&orientation=landscape',
  replacement: 'https://readdy.ai/api/search-image?query=luxury%20new%20wide%20plank%20hardwood%20floor%20replacement%20modern%20elegant%20Maryland%20home%20bright%20interior%20white%20walls%20beautiful%20fresh%20install&width=600&height=400&seq=grid-repl-1&orientation=landscape',
};

const serviceHighlights: Record<string, string[]> = {
  installation: ['Custom wood species selection', 'Precision fit & finish', 'Subfloor preparation included'],
  refinishing: ['Restores like-new appearance', 'Up to 70% savings vs. replacement', 'Same-week scheduling available'],
  repair: ['Seamless color & grain matching', 'Water damage specialists', 'Squeak elimination'],
  replacement: ['Full tear-out & disposal', 'Premium species selection', 'Highest ROI flooring investment'],
};

interface ServicesGridProps {
  onEstimateClick: () => void;
}

export default function ServicesGrid({ onEstimateClick }: ServicesGridProps) {
  return (
    <section className="bg-sand py-28 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-end justify-between mb-14">
          <div>
            <span className="font-sans text-xs uppercase tracking-widest text-oak font-semibold">What We Do</span>
            <h2 className="font-serif text-4xl md:text-5xl text-walnut font-bold mt-2 leading-tight">
              Expert Hardwood Flooring Services
            </h2>
            <p className="font-sans text-charcoal/60 text-base mt-4 max-w-xl">
              From brand-new installations to complete floor restoration — Maryland&apos;s most trusted hardwood specialists.
            </p>
          </div>
          <Link
            to="/services"
            className="hidden sm:flex items-center gap-2 text-oak font-sans text-sm font-semibold hover:text-walnut transition-colors whitespace-nowrap border-b border-oak/30 hover:border-walnut pb-0.5"
          >
            View All Services <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {servicesMeta.map((service) => (
            <Link
              key={service.id}
              to={`/services/${service.slug}`}
              className="group bg-white rounded-2xl overflow-hidden border border-sand-dark hover:border-oak/30 hover:-translate-y-2 transition-all duration-300 cursor-pointer"
            >
              {/* Image */}
              <div className="h-44 overflow-hidden relative">
                <img
                  src={serviceImages[service.id] || ''}
                  alt={service.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-walnut/60 to-transparent" />
                <div className="absolute bottom-3 left-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-gold rounded-lg">
                    <i className={`${service.icon} text-walnut text-base`} />
                  </div>
                </div>
              </div>
              {/* Content */}
              <div className="p-6">
                <h3 className="font-serif text-lg font-bold text-walnut mb-2 leading-snug group-hover:text-oak transition-colors">
                  {service.name}
                </h3>
                <p className="font-sans text-sm text-charcoal/60 leading-relaxed mb-4 line-clamp-2">
                  {service.shortDesc}
                </p>
                {/* Highlights */}
                <ul className="space-y-1.5 mb-5">
                  {(serviceHighlights[service.id] || []).map((h) => (
                    <li key={h} className="flex items-center gap-2 text-xs font-sans text-charcoal/70">
                      <i className="ri-check-line text-gold flex-shrink-0" />
                      {h}
                    </li>
                  ))}
                </ul>
                <div className="flex items-center gap-1.5 text-oak font-sans text-sm font-semibold group-hover:gap-3 transition-all">
                  Learn More <i className="ri-arrow-right-line" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Inline CTA */}
        <div className="bg-walnut rounded-2xl p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-serif text-2xl text-white font-bold mb-2">Not Sure Which Service You Need?</h3>
            <p className="text-white/65 font-sans text-sm">
              Our specialists assess your floors during a free in-home visit and recommend the exact right solution.
            </p>
          </div>
          <div className="flex gap-3 flex-shrink-0">
            <button
              onClick={onEstimateClick}
              className="px-8 py-3.5 bg-gold text-walnut font-bold rounded-xl hover:bg-gold-dark transition-colors font-sans text-sm whitespace-nowrap"
            >
              Schedule Free Assessment
            </button>
            <a href="tel:+14105550192" className="px-8 py-3.5 border-2 border-white/30 text-white font-medium rounded-xl hover:bg-white/10 transition-colors font-sans text-sm whitespace-nowrap flex items-center gap-2">
              <i className="ri-phone-line" /> Call Now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
